import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Apple, Coffee, Wine, Milk, Cookie, Sandwich, 
  CreditCard, Banknote, Receipt, Ticket, MapPin,
  Cigarette, Package, Droplets, IceCream, ShoppingBag
} from 'lucide-react';

const Products = () => {
  const productCategories = [
    {
      icon: Coffee,
      title: 'Beverages & Coffee',
      description: 'Hot coffee, fountain drinks & energy drinks',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/upjqcsds_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%283%29.jpeg',
      items: ['Fountain Drinks', 'Hot Coffee', 'Energy Drinks', 'Bottled Water'],
    },
    {
      icon: Wine,
      title: 'Beer & Wine',
      description: 'Quality selection of alcoholic beverages',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/7mql3ceq_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%281%29.jpeg',
      items: ['Domestic Beer', 'Imported Beer', 'Red Wine', 'White Wine'],
    },
    {
      icon: Cookie,
      title: 'Snacks & Candy',
      description: 'Sweet & savory treats',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/mkasgc67_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM.jpeg',
      items: ['Chips & Crackers', 'Cookies', 'Candy Bars', 'Nuts & Seeds'],
    },
    {
      icon: ShoppingBag,
      title: 'Everyday Essentials',
      description: 'Everything you need',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/6m4ot12p_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%284%29.jpeg',
      items: ['Groceries', 'Personal Care', 'Household Items', 'Phone Accessories'],
    },
    {
      icon: Milk,
      title: 'Dairy Products',
      description: 'Fresh milk, cheese, and more',
      image: 'https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=400&h=300&fit=crop',
      items: ['Milk', 'Cheese', 'Yogurt', 'Butter & Cream'],
    },
    {
      icon: IceCream,
      title: 'Frozen Items',
      description: 'Ice cream, frozen foods & treats',
      image: 'https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=400&h=300&fit=crop',
      items: ['Ice Cream', 'Frozen Meals', 'Frozen Desserts', 'Ice'],
    },
    {
      icon: Apple,
      title: 'Fresh Food',
      description: 'Temporarily Unavailable',
      image: 'https://images.unsplash.com/photo-1605447813584-26aeb3f8e6ae?w=400&h=300&fit=crop',
      items: ['Coming Soon'],
      unavailable: true,
    },
    {
      icon: Package,
      title: 'Household Items',
      description: 'Cleaning supplies & essentials',
      image: 'https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=400&h=300&fit=crop',
      items: ['Cleaning Supplies', 'Paper Products', 'Batteries', 'Light Bulbs'],
    },
  ];

  const services = [
    {
      icon: CreditCard,
      title: 'ATM Available',
      description: 'Convenient 24/7 cash access inside the store',
      highlight: true,
    },
    {
      icon: Banknote,
      title: 'Bill Payment',
      description: 'Pay your utility bills quickly and easily',
      highlight: true,
    },
    {
      icon: Ticket,
      title: 'Lottery Tickets',
      description: 'Try your luck with CA Lottery games',
      highlight: false,
    },
    {
      icon: Receipt,
      title: 'EBT / SNAP',
      description: 'We proudly accept EBT and SNAP benefits',
      highlight: true,
    },
    {
      icon: Cigarette,
      title: 'Tobacco Products',
      description: 'Cigarettes and tobacco for adults 21+',
      highlight: false,
    },
    {
      icon: Droplets,
      title: 'Propane Exchange',
      description: 'Tank exchange for your grill or heater',
      highlight: false,
    },
  ];

  return (
    <div className="grain-bg" data-testid="products-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#1A4D2E]" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up">
              Products & Services
            </h1>
            <p className="text-xl text-white/80 animate-fade-in-up animation-delay-100">
              Everything you need, all in one convenient location. From fresh groceries to everyday services.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-24" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#E07A5F] font-semibold uppercase tracking-wider text-sm">
              Shop With Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3D405B] mt-2">
              Our Product Selection
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCategories.map((category, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl overflow-hidden shadow-lg card-hover group ${category.unavailable ? 'opacity-75' : ''}`}
                data-testid={`product-category-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {category.unavailable && (
                    <div className="absolute top-3 right-3 bg-[#E07A5F] text-white text-xs font-bold px-2 py-1 rounded-full">
                      Temporarily Out
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <category.icon size={20} className="text-[#1A4D2E]" />
                    </div>
                    <h3 className="text-white font-bold text-lg">{category.title}</h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className={`mb-4 ${category.unavailable ? 'text-[#E07A5F] font-medium' : 'text-[#5F6C7B]'}`}>
                    {category.description}
                  </p>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#3D405B]">
                        <span className={`w-1.5 h-1.5 rounded-full ${category.unavailable ? 'bg-gray-400' : 'bg-[#E07A5F]'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#E07A5F] font-semibold uppercase tracking-wider text-sm">
              Beyond Shopping
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3D405B] mt-2">
              Convenient Services
            </h2>
            <p className="text-[#5F6C7B] mt-4 max-w-2xl mx-auto">
              We're more than just a store – take advantage of our helpful services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className={`rounded-2xl p-6 ${
                  service.highlight
                    ? 'bg-[#1A4D2E] text-white'
                    : 'bg-[#F4F1DE] text-[#3D405B]'
                } card-hover`}
                data-testid={`service-item-${index}`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                  service.highlight ? 'bg-white/10' : 'bg-white'
                }`}>
                  <service.icon
                    size={28}
                    className={service.highlight ? 'text-[#E07A5F]' : 'text-[#1A4D2E]'}
                  />
                </div>
                <h3 className="font-bold text-xl mb-2">{service.title}</h3>
                <p className={service.highlight ? 'text-white/80' : 'text-[#5F6C7B]'}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 md:py-24 bg-[#E07A5F]" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <ShoppingBag size={48} className="text-white mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Everything You Need, Close to Home
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Stop by Shah Market today and see our full selection. We're always adding new products to serve you better!
          </p>
          <Link
            to="/location"
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 text-lg hover:bg-white/90 transition-all"
            data-testid="products-cta-button"
          >
            <MapPin size={20} />
            Visit Us Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
